import { apiUrl } from "./http/apiConfiguration";
import { requestJson, requestWithoutContent, withQuery } from "./http/httpClient";
import { readNullableString, readObject } from "./http/jsonDecoding";

/**
 * People's pictures (F14; module A, `server/src/dmw/api/routes/accounts/avatars.py`). Each
 * person sets and removes their own; anyone signed in reads anyone's. The server owns the
 * rule on type and size; the browser sends the cropped square.
 */

const OWN_PATH = "/accounts/users/me/avatar";

function avatarPath(userId: string): `/${string}` {
  return `/accounts/users/${encodeURIComponent(userId)}/avatar`;
}

function decodeVersion(body: unknown): string | null {
  return readNullableString(readObject(body, "avatar"), "version");
}

/** The version of the person's picture, or `null` while they have none. */
export function readAvatarVersion(userId: string, signal?: AbortSignal): Promise<string | null> {
  return requestJson({ method: "GET", path: avatarPath(userId), signal, decodeResponse: decodeVersion });
}

/** Make `picture` the signed-in person's picture; resolves to its version. */
export async function replaceOwnAvatar(picture: Blob): Promise<string> {
  const version = await requestJson({
    method: "PUT",
    path: OWN_PATH,
    body: { kind: "file", content: picture, contentType: picture.type },
    decodeResponse: decodeVersion,
  });
  if (version === null) {
    throw new Error("The server stored a picture without a version.");
  }
  return version;
}

export function removeOwnAvatar(): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: OWN_PATH });
}

/** The picture's URL; the browser keeps it until the version changes. */
export function avatarPictureUrl(userId: string, version: string): string {
  return apiUrl(withQuery(`${avatarPath(userId)}/picture`, { version }));
}
